import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UseMutationResult } from 'react-query';
import { t } from 'i18next';
import {
  apiStatuses,
  getRecaptchaToken,
  IResponse,
  routesPaths,
} from '@selfserve/react-utils';
import { ModalSettings } from '@selfserve/react-hocs';
import { storage, storageKeys, useStorage } from './use-storage';

interface IReturn {
  removeSuccessMessage: () => void;
  onClickHandle: () => void;
  successMessage: string;
  email: string;
}

export const useEmailResend = <P, R extends { message_en?: string }>(
  openErrorModal: (settings?: ModalSettings) => void,
  resendRequest: () => UseMutationResult<IResponse<R>, unknown, P, unknown>,
  isLogin = false,
  productFamily?: string,
): IReturn => {
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState('');
  const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(false);
  const [sendingTime, setSendingTime] = useState(0);
  const [sessionEmail] = useStorage(storage.SESSION, storageKeys.EMAIL);
  const [sessionPackageId] = useStorage(
    storage.SESSION,
    storageKeys.PACKAGE_ID
  );
  const {
    data: response,
    mutateAsync,
    isSuccess,
  } = resendRequest();

  const removeSuccessMessage = useCallback(() => setSuccessMessage(''), []);

  const onClickHandle = useCallback(() => {
    const currentTime = new Date().getTime();
    const timeExpired = currentTime - sendingTime > 60000;
    if ((!sendingTime || timeExpired) && !isRecaptchaLoading) {
      setIsRecaptchaLoading(true);
      getRecaptchaToken().then((recaptchaToken: string) => {
        const payload = {
          ...(!isLogin && {
            desiredPackageId: sessionPackageId,
            resend: true,
          }),
          ...(isLogin && productFamily && { productFamily }),
          recaptchaToken,
          email: sessionEmail,
        };
        mutateAsync(payload as P);
        setIsRecaptchaLoading(false);
      });
    } else if (!timeExpired && !successMessage) {
      setSuccessMessage(t('common.email-sent'));
      const timeout = setTimeout(() => {
        setSuccessMessage('');
        if (timeout) clearTimeout(timeout);
      }, 3000);
    }
  }, [
    mutateAsync,
    sessionPackageId,
    isRecaptchaLoading,
    sessionEmail,
    successMessage,
    sendingTime,
    productFamily,
  ]);

  useEffect(() => {
    const { data, status } = response || {};
    let timeout: NodeJS.Timeout | null = null;
    if (status) {
      if (status === apiStatuses.CREATED) {
        setSuccessMessage(t('common.email-sent'));
        setSendingTime(new Date().getTime());
        timeout = setTimeout(() => {
          setSuccessMessage('');
          if (timeout) clearTimeout(timeout);
        }, 3000);
      } else {
        openErrorModal({ text: data?.message_en });
      }
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [response, isSuccess, openErrorModal]);

  useEffect(() => {
    if (isLogin ? !sessionEmail : !sessionEmail || !sessionPackageId) {
      navigate(routesPaths.LOGIN);
    }
  }, [sessionEmail, sessionPackageId, navigate]);

  return {
    removeSuccessMessage,
    onClickHandle,
    successMessage,
    email: sessionEmail,
  };
};
